import React, { useCallback, useContext, useMemo, useState } from 'react';
import { Grid, MenuItem, TextField } from '@material-ui/core';
import { css } from '@emotion/react';
import { UserContext } from '@/components/App';
import CustomizableTable from '@/components/CustomizableTable';
import CustomizableTableProfileControl from '@/components/CustomizableTable/CustomizableTableProfileControl';
import { useOrderTableProfiles } from '@/hooks';
import { COLUMNS } from './constants';

const DEFAULT_TABLE_INITIAL_STATE = {
    hiddenColumns: [],
    columnOrder: [],
    columnResizing: { columnWidths: {} },
};

function OrderItems({ order, resolutionRemovedItems, callbacks }) {
    const { user } = useContext(UserContext);

    const [tableInitialState, setTableInitialState] = useState(DEFAULT_TABLE_INITIAL_STATE);
    const [tableKey, setTableKey] = useState(0);
    const [tableLayoutState, setTableLayoutState] = useState({
        hiddenColumns: [],
        columnOrder: [],
        columnWidths: {},
    });
    const [sortBy, setSortBy] = useState(null);

    const tableData = useMemo(() => {
        const regularItems = (order?.itemsByOrderId || []).map((item) => ({ ...item, order }));
        const removedItems = (resolutionRemovedItems || []).map((mapping) => ({
            ...mapping.item,
            order,
            isResolutionRemoved: true,
        }));
        return [...regularItems, ...removedItems];
    }, [order, resolutionRemovedItems]);

    const buildProfileSettings = useCallback(() => {
        return {
            layout: {
                hiddenColumns: tableLayoutState.hiddenColumns || [],
                columnOrder: tableLayoutState.columnOrder || [],
                columnWidths: tableLayoutState.columnWidths || {},
            },
            sorting: {
                sortBy: sortBy ? [{ id: sortBy.id, desc: !!sortBy.desc }] : [],
            },
        };
    }, [sortBy, tableLayoutState]);

    const applyProfileSettings = useCallback((settings = {}) => {
        const layout = settings?.layout || {};
        setTableInitialState({
            hiddenColumns: layout.hiddenColumns || [],
            columnOrder: layout.columnOrder || [],
            columnResizing: { columnWidths: layout.columnWidths || {} },
        });
        setTableKey((k) => k + 1);

        const nextSort = settings?.sorting?.sortBy?.[0];
        if (nextSort?.id) {
            setSortBy({ id: nextSort.id, desc: !!nextSort.desc });
        } else {
            setSortBy(null);
        }
    }, []);

    const resetToDefaults = useCallback(() => {
        setTableInitialState(DEFAULT_TABLE_INITIAL_STATE);
        setTableKey((k) => k + 1);
        setSortBy(null);
    }, []);

    const {
        profiles,
        profilesLoading,
        selectedProfileId,
        currentProfileName,
        currentProfileId,
        profileSelectProps,
        handleSaveNewProfile,
        handleOverwriteProfile,
        handleImportProfileById,
        handleDeleteCurrentProfile,
        insertProfileLoading,
        updateProfileLoading,
        deleteProfileLoading,
    } = useOrderTableProfiles({
        tableType: 'ORDER_DETAILS_ITEMS',
        userId: user?.user_id,
        buildProfileSettings,
        applyProfileSettings,
        onResetToDefault: resetToDefaults,
        notifyError: (message) => callbacks?.setNotification?.({ severity: 'error', message }),
    });

    const profileControls = useMemo(
        () => (
            <CustomizableTableProfileControl
                disabled={profilesLoading}
                tableType="ORDER_DETAILS_ITEMS"
                currentProfileName={currentProfileName}
                currentProfileId={currentProfileId}
                selectedProfileId={selectedProfileId}
                onSaveNew={handleSaveNewProfile}
                onOverwrite={handleOverwriteProfile}
                onImportById={handleImportProfileById}
                onDeleteCurrent={handleDeleteCurrentProfile}
                saving={insertProfileLoading}
                overwriting={updateProfileLoading}
                deleting={deleteProfileLoading}
            />
        ),
        [
            currentProfileId,
            currentProfileName,
            deleteProfileLoading,
            handleDeleteCurrentProfile,
            handleImportProfileById,
            handleOverwriteProfile,
            handleSaveNewProfile,
            insertProfileLoading,
            profilesLoading,
            selectedProfileId,
            updateProfileLoading,
        ]
    );

    return (
        <>
            <Grid item sm={1}></Grid>
            <Grid
                item
                xs={10}
                css={css`
                    height: 100%;
                    margin-top: 3rem;
                `}
            >
                <Grid
                    container
                    direction="column"
                    css={css`
                        height: 100%;
                        padding-bottom: 2rem;
                        max-width: 100%;
                        overflow: hidden;
                    `}
                >
                    <Grid
                        container
                        direction="row"
                        alignItems="center"
                        css={css`
                            margin-bottom: 12px;
                            gap: 16px;
                        `}
                    >
                        <Grid
                            item
                            css={css`
                                padding-top: 10px;
                                min-width: 200px;
                            `}
                        >
                            <TextField
                                select
                                variant="outlined"
                                fullWidth
                                size="small"
                                label={profiles?.length ? 'Profile' : 'Select Profile...'}
                                InputLabelProps={{ shrink: true }}
                                {...profileSelectProps}
                                css={css`
                                    background-color: white;
                                `}
                            >
                                <MenuItem value="">Default</MenuItem>
                                {profiles.map((profile) => (
                                    <MenuItem
                                        key={profile.order_table_profile_id}
                                        value={profile.order_table_profile_id}
                                    >
                                        {profile.name}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item>{profileControls}</Grid>
                    </Grid>

                    <Grid
                        item
                        css={css`
                            flex: 1 1 0;
                            min-height: 0;
                            min-width: 0;
                            max-width: 100%;
                            display: flex;
                            flex-direction: column;
                        `}
                    >
                        <CustomizableTable
                            key={tableKey}
                            data={tableData}
                            columns={COLUMNS}
                            sortBy={sortBy ? [sortBy] : []}
                            onSortChange={(newSortBy) => {
                                if (newSortBy?.id !== sortBy?.id || newSortBy?.desc !== sortBy?.desc) {
                                    setSortBy(newSortBy || null);
                                }
                            }}
                            getRowId={(row, index) => row.item_id || `removed-${index}`}
                            initialState={tableInitialState}
                            onLayoutChange={setTableLayoutState}
                            wrapBodyCellContent={true}
                            manualSortBy={false}
                            cellRenderContext={{ callbacks }}
                        />
                    </Grid>
                </Grid>
            </Grid>
            <Grid item sm={1}></Grid>
        </>
    );
}

export default OrderItems;
